import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { EmbeddedSignupExchangeResponse, MetaOnboardingConfig } from '../../models/api.models';
import { SetupService } from '../../services/setup.service';

declare global {
  interface Window {
    FB?: {
      init: (options: Record<string, unknown>) => void;
      login: (
        callback: (response: {
          authResponse?: { code?: string };
          status?: string;
        }) => void,
        options: Record<string, unknown>
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

@Component({
  selector: 'app-setup-wizard-page',
  templateUrl: './setup-wizard.page.html',
  styleUrls: ['./setup-wizard.page.scss'],
})
export class SetupWizardPage implements OnInit, OnDestroy {
  currentStep = 1;
  isSubmitting = false;
  isConnectingFacebook = false;
  configError = '';
  connectMessage = '';
  successMessage = '';
  metaConfig: MetaOnboardingConfig | null = null;
  readonly form: UntypedFormGroup;
  private readonly messageHandler = (event: MessageEvent) => this.handleEmbeddedSignupMessage(event);
  private pendingSignupCode: string | null = null;
  private pendingSignupState: string | null = null;
  private pendingEmbeddedData: {
    waba_id?: string;
    phone_number_id?: string;
    phone_number?: string;
  } = {};
  private facebookSdkReady = false;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly setupService: SetupService
  ) {
    this.form = this.formBuilder.group({
      businessName: ['', [Validators.required]],
      businessEmail: ['', [Validators.required, Validators.email]],
      businessPhone: ['', [Validators.required]],
      facebookBusinessId: ['', [Validators.required]],
      wabaId: [''],
      metaAccessToken: [''],
      phoneNumber: ['', [Validators.required]],
      phoneNumberId: ['', [Validators.required]],
      verificationCode: ['', [Validators.required, Validators.minLength(6)]],
      coexistenceEnabled: [true],
    });
  }

  ngOnInit(): void {
    window.addEventListener('message', this.messageHandler);

    this.setupService.metaConfig().subscribe({
      next: (config) => {
        this.metaConfig = config;
        if (config.business_portfolio_id) {
          this.form.patchValue({
            facebookBusinessId: config.business_portfolio_id,
          });
        }
        this.ensureFacebookSdk(config);
      },
      error: () => {
        this.configError = 'Meta configuration is not available on the server yet.';
      },
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('message', this.messageHandler);
  }

  nextStep(): void {
    this.currentStep = Math.min(this.currentStep + 1, 4);
  }

  previousStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
  }

  connectFacebook(): void {
    if (!this.metaConfig?.is_ready) {
      this.connectMessage = 'Meta embedded signup is not ready yet. Add the Configuration ID on the backend first.';
      return;
    }

    if (!this.facebookSdkReady || !window.FB) {
      this.connectMessage = 'Facebook SDK is still loading. Please try again in a moment.';
      return;
    }

    this.isConnectingFacebook = true;
    this.connectMessage = '';
    this.pendingSignupCode = null;
    this.pendingSignupState = null;
    this.pendingEmbeddedData = {};

    this.setupService.createEmbeddedSignupSession().subscribe({
      next: (session) => {
        if (!session.is_ready) {
          this.isConnectingFacebook = false;
          this.connectMessage = `Embedded signup is missing: ${session.missing.join(', ')}.`;
          return;
        }

        this.pendingSignupState = session.state;

        window.FB?.login((response) => {
          const code = response?.authResponse?.code ?? null;

          if (!code) {
            this.isConnectingFacebook = false;
            this.connectMessage = 'Facebook login did not return an authorization code.';
            return;
          }

          this.pendingSignupCode = code;
          this.tryFinalizeEmbeddedSignup();
        }, {
          config_id: this.metaConfig?.configuration_id,
          response_type: 'code',
          override_default_response_type: true,
          state: session.state,
          extras: {
            setup: {},
            featureType: 'whatsapp_business_app_onboarding',
            sessionInfoVersion: '3',
            version: 'v4',
          },
        });
      },
      error: () => {
        this.isConnectingFacebook = false;
        this.connectMessage = 'Unable to start Meta embedded signup right now.';
      },
    });
  }

  private handleEmbeddedSignupMessage(event: MessageEvent): void {
    const isTrustedMetaOrigin = event.origin === 'https://www.facebook.com' || event.origin === window.location.origin;
    if (!isTrustedMetaOrigin) {
      return;
    }

    if (typeof event.data === 'string') {
      try {
        const metaPayload = JSON.parse(event.data) as {
          type?: string;
          event?: string;
          data?: {
            waba_id?: string;
            phone_number_id?: string;
            display_phone_number?: string;
            current_step?: string;
          };
        };

        if (metaPayload.type === 'WA_EMBEDDED_SIGNUP') {
          if (metaPayload.event === 'FINISH') {
            this.pendingEmbeddedData = {
              waba_id: metaPayload.data?.waba_id,
              phone_number_id: metaPayload.data?.phone_number_id,
              phone_number: metaPayload.data?.display_phone_number,
            };
            this.tryFinalizeEmbeddedSignup();
            return;
          }

          if (metaPayload.event === 'CANCEL') {
            this.isConnectingFacebook = false;
            this.connectMessage = `Facebook signup was cancelled at ${metaPayload.data?.current_step ?? 'an unknown step'}.`;
            return;
          }

          if (metaPayload.event === 'ERROR') {
            this.isConnectingFacebook = false;
            this.connectMessage = 'Facebook embedded signup returned an error.';
            return;
          }
        }
      } catch {
        // Ignore non-JSON messages and let the callback-page flow below handle same-origin payloads.
      }
    }

    const payload = event.data as {
      type?: string;
      code?: string | null;
      state?: string | null;
      error?: string | null;
      error_description?: string | null;
    };

    if (payload?.type !== 'meta-embedded-signup') {
      return;
    }

    if (payload.error) {
      this.isConnectingFacebook = false;
      this.connectMessage = payload.error_description || 'Facebook connection failed.';
      return;
    }

    if (!payload.code || !payload.state) {
      this.isConnectingFacebook = false;
      this.connectMessage = 'Facebook did not return an authorization code.';
      return;
    }

    this.pendingSignupCode = payload.code;
    this.pendingSignupState = payload.state;
    this.tryFinalizeEmbeddedSignup();
  }

  private applyEmbeddedSignupResult(response: EmbeddedSignupExchangeResponse): void {
    this.form.patchValue({
      facebookBusinessId: response.business_manager_id,
      wabaId: response.waba_id,
      phoneNumber: response.phone_number,
      phoneNumberId: response.phone_number_id,
      metaAccessToken: '',
    });
  }

  private ensureFacebookSdk(config: MetaOnboardingConfig): void {
    if (this.facebookSdkReady || !config.app_id) {
      return;
    }

    const initialize = (): void => {
      if (!window.FB) {
        return;
      }

      window.FB.init({
        appId: config.app_id,
        cookie: true,
        xfbml: false,
        version: config.graph_version,
      });
      this.facebookSdkReady = true;
    };

    if (window.FB) {
      initialize();
      return;
    }

    window.fbAsyncInit = initialize;

    if (document.getElementById('facebook-jssdk')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.async = true;
    script.defer = true;
    script.src = 'https://connect.facebook.net/en_US/sdk.js';
    document.body.appendChild(script);
  }

  private tryFinalizeEmbeddedSignup(): void {
    if (!this.pendingSignupCode || !this.pendingSignupState) {
      return;
    }

    this.setupService.exchangeEmbeddedSignup({
      code: this.pendingSignupCode,
      state: this.pendingSignupState,
      ...this.pendingEmbeddedData,
    }).subscribe({
      next: (response) => {
        this.applyEmbeddedSignupResult(response);
        this.pendingSignupCode = null;
        this.pendingSignupState = null;
        this.pendingEmbeddedData = {};
        this.isConnectingFacebook = false;
        this.connectMessage = 'WhatsApp coexistence details were fetched from Meta successfully.';
        this.currentStep = Math.max(this.currentStep, 3);
      },
      error: () => {
        this.isConnectingFacebook = false;
        this.connectMessage = 'Meta connection returned, but token exchange failed on the backend.';
      },
    });
  }

  validateManualFacebookConnection(): void {
    const payload = {
      business_manager_id: this.form.controls.facebookBusinessId.value,
      access_token: this.form.controls.metaAccessToken.value,
    };

    this.isConnectingFacebook = true;
    this.connectMessage = '';

    this.setupService.connectFacebook(payload).subscribe({
      next: (response) => {
        this.isConnectingFacebook = false;
        this.connectMessage = `Connected to business portfolio ${response.business_manager_id}.`;
      },
      error: () => {
        this.isConnectingFacebook = false;
        this.connectMessage = 'Meta validation failed. Check the access token and business permissions.';
      },
    });
  }

  submit(): void {
    if (this.form.invalid || this.isSubmitting) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.successMessage = '';

    this.setupService.completeOnboarding(this.form.getRawValue()).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.successMessage = 'WhatsApp onboarding saved successfully.';
      },
      error: () => {
        this.isSubmitting = false;
      },
    });
  }
}
